import {useEffect, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ExternalLink, Search, Settings2} from "lucide-react";
import QRCode from "react-qr-code";
import {mapApiError} from "../../shared/api/errors";
import {
    findReferralUser,
    getReferralDetail,
    referralKeys,
    updateReferralCredentials,
    updateReferralDetail,
} from "./api";
import {normalizeCredentialDraft, validateCredentialDraft, validateRefCode} from "./contracts";
import {ReferralDialog, ReferralDialogError} from "./ReferralDialog";
import {useTranslation} from "../../shared/i18n";


const EMPTY_LEVELS = Object.fromEntries(Array.from({length: 7}, (_, index) => [`level${index + 1}`, "0"]));

export const CredentialEditorDialog = ({open, credentials, onClose}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [draft, setDraft] = useState(EMPTY_LEVELS);
    const [validationError, setValidationError] = useState("");
    const [saved, setSaved] = useState(false);
    const mutation = useMutation({
        mutationFn: updateReferralCredentials,
        onSuccess: (response) => {
            queryClient.setQueryData(referralKeys.credentials(), response);
            void queryClient.invalidateQueries({queryKey: referralKeys.credentials()});
            setDraft(normalizeCredentialDraft(response));
            setSaved(true);
        },
    });
    const resetMutation = mutation.reset;

    useEffect(() => {
        if (!open) return;
        setDraft(normalizeCredentialDraft(credentials));
        setValidationError("");
        setSaved(false);
        resetMutation();
    }, [credentials, open, resetMutation]);

    const submit = (event) => {
        event.preventDefault();
        const validation = validateCredentialDraft(draft);
        setValidationError(validation.error);
        setSaved(false);
        if (!validation.error) mutation.mutate(validation.values);
    };

    return (
        <ReferralDialog open={open} title={t("sections.referrals.p29")} eyebrow={t("sections.referrals.p30")} pending={mutation.isPending} onClose={onClose}>
            <form className="referral-dialog-form" onSubmit={submit}>
                <p className="referral-dialog-copy">{t("sections.referrals.p31")}</p>
                <div className="referral-level-grid">
                    {Array.from({length: 7}, (_, index) => {
                        const name = `level${index + 1}`;
                        return (
                            <label key={name}>
                                <span>{t("sections.referrals.p32")} {index + 1} (%)</span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={draft[name]}
                                    disabled={mutation.isPending}
                                    onChange={(event) => setDraft((current) => ({...current, [name]: event.target.value}))}
                                />
                            </label>
                        );
                    })}
                </div>
                {validationError ? <div className="referral-dialog-error" role="alert">{validationError}</div> : null}
                <ReferralDialogError result={mutation.isError ? mapApiError(mutation.error) : null} />
                {saved ? <p className="referral-dialog-success" role="status">{t("sections.referrals.p33")}</p> : null}
                <footer className="referral-dialog-footer">
                    <button type="button" className="admin-button admin-button-secondary" disabled={mutation.isPending} onClick={onClose}>{t("sections.referrals.p7")}</button>
                    <button type="submit" className="admin-button admin-button-primary" disabled={mutation.isPending}>
                        <Settings2 aria-hidden="true" size={15} /> {mutation.isPending ? t("sections.referrals.p34") : t("sections.referrals.p35")}
                    </button>
                </footer>
            </form>
        </ReferralDialog>
    );
};

export const GlobalReferralSearchDialog = ({open, countryId, onClose, onFound}) => {
    const {t} = useTranslation();
    const [phoneNumber, setPhoneNumber] = useState("");
    const [validationError, setValidationError] = useState("");
    const mutation = useMutation({
        mutationFn: () => findReferralUser({phoneNumber, countryId}),
        onSuccess: (response) => onFound(response.link),
    });
    const resetMutation = mutation.reset;

    useEffect(() => {
        if (!open) return;
        setPhoneNumber("");
        setValidationError("");
        resetMutation();
    }, [open, resetMutation]);

    const submit = (event) => {
        event.preventDefault();
        if (!phoneNumber.trim()) {
            setValidationError(t("sections.referrals.p36"));
            return;
        }
        setValidationError("");
        mutation.mutate();
    };

    return (
        <ReferralDialog open={open} title={t("sections.referrals.p37")} eyebrow={t("sections.referrals.p38")} pending={mutation.isPending} onClose={onClose}>
            <form className="referral-dialog-form" onSubmit={submit}>
                <label className="referral-field">
                    <span>{t("sections.referrals.p3")}</span>
                    <input value={phoneNumber} disabled={mutation.isPending} onChange={(event) => setPhoneNumber(event.target.value)} />
                </label>
                {validationError ? <div className="referral-dialog-error" role="alert">{validationError}</div> : null}
                <ReferralDialogError result={mutation.isError ? mapApiError(mutation.error) : null} />
                <footer className="referral-dialog-footer">
                    <button type="button" className="admin-button admin-button-secondary" disabled={mutation.isPending} onClick={onClose}>{t("sections.referrals.p7")}</button>
                    <button type="submit" className="admin-button admin-button-primary" disabled={mutation.isPending}>
                        <Search aria-hidden="true" size={15} /> {mutation.isPending ? t("sections.referrals.p39") : t("sections.referrals.p40")}
                    </button>
                </footer>
            </form>
        </ReferralDialog>
    );
};

const downloadQr = (container, fileName) => {
    const svg = container?.querySelector("svg");
    if (!svg) return;
    const blob = new Blob([new XMLSerializer().serializeToString(svg)], {type: "image/svg+xml;charset=utf-8"});
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
};

export const ReferralDetailDialog = ({row, countryId, canManage, onClose}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const qrRef = useRef(null);
    const refId = row?.id;
    const [refCode, setRefCode] = useState("");
    const [validationError, setValidationError] = useState("");
    const [saved, setSaved] = useState(false);
    const detailQuery = useQuery({
        queryKey: referralKeys.detail(countryId, refId),
        queryFn: () => getReferralDetail({refId, countryId}),
        enabled: Boolean(refId),
    });
    const mutation = useMutation({
        mutationFn: (value) => updateReferralDetail({refId, refCode: value, countryId}),
        onSuccess: (response) => {
            queryClient.setQueryData(referralKeys.detail(countryId, refId), response);
            void queryClient.invalidateQueries({queryKey: referralKeys.detail(countryId, refId)});
            void queryClient.invalidateQueries({queryKey: referralKeys.all(countryId)});
            setRefCode(response.ref_code || "");
            setSaved(true);
        },
    });
    const resetMutation = mutation.reset;

    useEffect(() => {
        if (!refId) return;
        setRefCode("");
        setValidationError("");
        setSaved(false);
        resetMutation();
    }, [refId, resetMutation]);

    useEffect(() => {
        if (detailQuery.data) setRefCode(detailQuery.data.ref_code || "");
    }, [detailQuery.data]);

    const link = Array.isArray(detailQuery.data?.ref_link) ? detailQuery.data.ref_link[0] : detailQuery.data?.ref_link;
    const submit = (event) => {
        event.preventDefault();
        const validation = validateRefCode(refCode);
        setValidationError(validation.error);
        setSaved(false);
        if (!validation.error) mutation.mutate(validation.value);
    };

    return (
        <ReferralDialog
            open={Boolean(row)}
            title={t("sections.referrals.p41")}
            eyebrow={row?.name || `#${refId || ""}`}
            pending={mutation.isPending}
            onClose={onClose}
        >
            {detailQuery.isLoading ? <p className="referral-dialog-copy">{t("sections.referrals.p42")}</p> : null}
            {detailQuery.isError ? <ReferralDialogError result={mapApiError(detailQuery.error)} /> : null}
            {detailQuery.isSuccess ? (
                <form className="referral-dialog-form" onSubmit={submit}>
                    {link ? (
                        <div className="referral-qr-layout">
                            <div ref={qrRef} className="referral-qr-code"><QRCode size={132} value={link} /></div>
                            <div>
                                <a href={link} target="_blank" rel="noreferrer">
                                    {t("sections.referrals.p43")} <ExternalLink aria-hidden="true" size={14} />
                                </a>
                                <button type="button" className="admin-button admin-button-secondary" onClick={() => downloadQr(qrRef.current, `referral-${refId}.svg`)}>
                                    {t("sections.referrals.p44")}
                                </button>
                            </div>
                        </div>
                    ) : null}
                    <label className="referral-field">
                        <span>{t("sections.referrals.p45")}</span>
                        <input maxLength={8} value={refCode} disabled={!canManage || mutation.isPending} onChange={(event) => setRefCode(event.target.value)} />
                    </label>
                    {validationError ? <div className="referral-dialog-error" role="alert">{validationError}</div> : null}
                    <ReferralDialogError result={mutation.isError ? mapApiError(mutation.error) : null} />
                    {saved ? <p className="referral-dialog-success" role="status">{t("sections.referrals.p46")}</p> : null}
                    <footer className="referral-dialog-footer">
                        <button type="button" className="admin-button admin-button-secondary" disabled={mutation.isPending} onClick={onClose}>{t("sections.referrals.p28")}</button>
                        {canManage ? (
                            <button type="submit" className="admin-button admin-button-primary" disabled={mutation.isPending}>
                                {mutation.isPending ? t("sections.referrals.p34") : t("sections.referrals.p47")}
                            </button>
                        ) : null}
                    </footer>
                </form>
            ) : null}
        </ReferralDialog>
    );
};
